import { combine, createEffect, createEvent, createStore } from 'effector';
import { get } from 'lodash';
import update from 'immutability-helper';
import api from '@constants/endpoints';
import createFiltersStore from './filters';
import request from '@helpers/request';
import localeWeight, { LOCALE } from './localeWeight';
import createPaginationStore from './pagination';
import { IPromotionGames, IPromotionGamesAction } from '@interfaces/IPromotionGames';
import moment, { Moment } from 'moment';
import createPreloadersStore from './preloaders';
import {
  mapDataToFieldsValue,
  mapValueToRequest,
} from '@pages/PromotionPage/Games/GamesEditModal/GamePromotionEditModal.helpers';
import { closeModal } from './modals';

export const reset = createEvent('Promotion store reset');

interface dateProperties {
  from: string;
  to: string;
}

const defaultFilters = {
  statusFilter: 'active',
  afterFilter: undefined,
  beforeFilter: undefined,
  gameNameFilter: '',
  positionLTE: '',
  positionGTE: '',
  sort: 'STARTS_ASC'
};

export const getList = createEffect({
  name: 'getList',
  async handler() {
    const { current, ...other } = pagination.getState();
    const { afterFilter, beforeFilter, statusFilter, gameNameFilter, sort, positionLTE, positionGTE } = filters.getState();
    const compare = (operation: string, dateOne: Moment | null, dateTwo: Moment | null) => {
      if (!dateOne) {
        return dateTwo;
      }
      if (!dateTwo) {
        return dateOne;
      }
      if (operation === 'max') {
        return dateOne.isBefore(dateTwo) ? dateTwo : dateOne;
      }
      if (operation === 'min') {
        return dateOne.isBefore(dateTwo) ? dateOne : dateTwo;
      }
    };

    const timeLineFilter = (status: string, range: dateProperties) => {
      const { from, to } = range;
      const activeDate = status === 'active' ? moment() : null;
      return {
        startsBeforeFilter: compare('min', activeDate, to ? moment(to) : null)?.format(),
        startsAfterFilter: status === 'planned' ? moment().format() : null,
        endsBeforeFilter: status === 'ended' ? moment().format() : null,
        endsAfterFilter: compare('max', activeDate, from ? moment(from) : null)?.format(),
      };
    };

    const data = {
      localeFilter: localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState(),
      page: {
        type: 'PAGE',
        ...other,
        page: (current || 1) - 1,
      },
      sort,
      positionLTE,
      positionGTE,
      gameNameFilter: gameNameFilter,
      ...timeLineFilter(statusFilter, { from: afterFilter, to: beforeFilter }),
    };
    const response = await request(api.promotion.getList, { data });
    return response;
  },
});

const updateListHandler = (
  state: IPromotionGames[],
  {
    result: {
      data: {
        body: { promo },
      },
    },
    params: { id },
  }: { params: { id?: number }; result: { data: { body: { promo: IPromotionGames } } } },
) => {
  if (id) {
    const foundIndex = state.findIndex((item) => item.id === id);
    if (foundIndex !== -1) {
      const updatedState: any = update(state, { [foundIndex]: { $set: promo } })
      return updatedState;
    }
  } else {
    getList();
  }
};

export const createPromotion = createEffect({
  name: 'createPromotion',
  async handler(data: IPromotionGamesAction) {
    const response = await request(api.promotion.create, { data });
    return response;
  },
});

export const editPromotion = createEffect({
  name: 'editPromotion',
  async handler(data: IPromotionGamesAction) {
    const response = await request(api.promotion.edit, {
      data,
      urlParams: { promoId: data.id?.toString() },
    })
    return response;
  },
});

export const finishPromotion = createEffect({
  name: 'finishPromotion',
  async handler(value: IPromotionGames) {
    const data = mapValueToRequest(mapDataToFieldsValue(value));
    const response = await request(api.promotion.edit, {
      data: {
        ...data,
        ends: moment().subtract(1, 'hours').format(),
      },
      urlParams: { promoId: data.id?.toString() },
    });
    return response;
  },
});

const preloaders = createPreloadersStore([getList]);

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'promotion',
  resetEvent: reset,
  bodyPath: ['data', 'body', 'page'],
});

export { setPagination };

const { filters, setFilters } = createFiltersStore({
  listName: 'promotion',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const list = createStore<IPromotionGames[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'page', 'page'], []))
  .on(createPromotion.done, updateListHandler)
  .on(editPromotion.done, () => {
    updateListHandler
    getList();
  })
  .on(finishPromotion.done, () => {
    updateListHandler;
    getList();
  })
  .reset(reset);

const promotion = combine({ list, preloaders, filters, setFilters, pagination, setPagination });

export default promotion;
